/// <reference types="google.maps" />
import React, { useEffect, useState } from "react";

import * as turf from "@turf/turf";
import { isAxiosError } from "axios";
import moment, { Moment } from "moment";

import { useFeatureFlag, useGetFieldShape, useLocation, useNavigate, useSplitField, useSplitFieldDateRestriction, useTranslation } from "utils";

import { FEATURE_FLAGS } from "consts";

import { Area, Coordinates, FieldDetails, Geometry } from "models";

import { Button, Col, DatePicker, Drawer, Flex, Icon, Link, MapInteractive, Modal, Row, Typography, Message as message } from "components";

type SplitFieldDrawerProps = {
  field: FieldDetails;
  geometry: Geometry;
  onClose: () => void;
  visible: boolean;
  enforceSubmissionRestriction?: boolean;
};

export const SplitFieldDrawer = ({ field, onClose, visible, enforceSubmissionRestriction = true }: SplitFieldDrawerProps) => {
  const { t } = useTranslation("farm");
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled: isViewSwitcherEnabled } = useFeatureFlag(FEATURE_FLAGS.FIELD_MANAGEMENT_VIEW_SWITCHER);

  const [splitAreas, setSplitAreas] = useState<Area[]>([]);
  const [fieldSplit, setFieldSplit] = useState(false);
  const [lineDrawn, setLineDrawn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [originalShape, setOriginalShape] = useState<Coordinates[]>([{ lat: 0, lng: 0 }]);
  const [shapes, setShapes] = useState<Coordinates[][]>([[{ lat: 0, lng: 0 }], [{ lat: 0, lng: 0 }]]);
  const [showModal, setShowModal] = useState(false);
  const [splitDate, setSplitDate] = useState<Moment>();

  const { mutateAsync: splitField } = useSplitField();
  const { fieldShape } = useGetFieldShape({ fieldId: field.id || "", highQuality: true });
  const { isDisabledDate, loading: restrictionLoading } = useSplitFieldDateRestriction();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setupDrawingAPI = (map: google.maps.Map, maps: any) => {
    map.setOptions({ draggableCursor: "crosshair", draggingCursor: "crosshair" });

    const points: google.maps.LatLng[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dotMarkers: any[] = [];

    const drawingPolyline = new maps.Polyline({
      strokeOpacity: 0,
      strokeColor: "#ffffff",
      strokeWeight: 2,
      icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: "#ffffff", scale: 2 }, offset: "0", repeat: "10px" }],
      clickable: false,
      editable: false,
      draggable: false,
      geodesic: true,
      map: map,
    });

    let clickListener: google.maps.MapsEventListener;

    const cleanup = () => {
      maps.event.removeListener(clickListener);
      drawingPolyline.setMap(null);
      dotMarkers.forEach((m) => m.setMap(null));
    };

    const completePolyline = () => {
      cleanup();

      setLoading(true);

      const geometry: number[][] = [];
      points.forEach((item) => {
        geometry.push([item.lat(), item.lng()]);
      });
      [...points].reverse().forEach((item) => {
        geometry.push([item.lat() + 0.000000001, item.lng() + 0.000000001]);
      });
      geometry.push(geometry[0]);

      try {
        const reducedShape = originalShape?.reduce((acc: number[][], shape) => [...acc, [shape.lat, shape.lng]], []);
        const originalPolygon = turf.polygon([reducedShape]);
        const splitPolygon = turf.polygon([geometry]);
        const difference = turf.difference(turf.featureCollection([originalPolygon, splitPolygon]));

        if (
          difference &&
          difference.geometry.coordinates.length === 2 &&
          difference.geometry.coordinates[0][0][0] instanceof Array &&
          difference.geometry.coordinates[1][0][0] instanceof Array
        ) {
          const newShapes = [[], []] as Coordinates[][];
          (difference.geometry.coordinates[0][0] as number[][]).map((coordinates) => newShapes[0].push({ lat: coordinates?.[0], lng: coordinates?.[1] }));
          (difference.geometry.coordinates[1][0] as number[][]).map((coordinates) => newShapes[1].push({ lat: coordinates?.[0], lng: coordinates?.[1] }));

          setShapes(newShapes);
          setFieldSplit(true);
          setLineDrawn(true);
          setTimeout(() => setLoading(false), 10);
        } else {
          setTimeout(() => setLoading(false), 10);
          message({
            content: t("global:errors.split"),
            icon: "error",
          });
        }
      } catch {
        setTimeout(() => setLoading(false), 10);
        message({
          content: t("global:errors.split"),
          icon: "error",
        });
      }
    };

    clickListener = maps.event.addListener(map, "click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        if (points.length === 0) {
          setLineDrawn(true);
        }
        points.push(e.latLng);
        drawingPolyline.setPath(points);

        dotMarkers.push(
          new maps.Marker({
            position: e.latLng,
            map: map,
            clickable: false,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 4,
              fillColor: "#ffffff",
              fillOpacity: 1,
              strokeColor: "transparent",
              strokeWeight: 0,
            },
          })
        );

        const ringMarker = new maps.Marker({
          position: e.latLng,
          map: map,
          clickable: false,
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 30,
            fillColor: "transparent",
            fillOpacity: 0,
            strokeColor: "#000000",
            strokeWeight: 1.5,
            strokeOpacity: 1,
          },
        });
        setTimeout(() => ringMarker.setMap(null), 500);

        if (points.length >= 2) {
          const prev = points[points.length - 2];
          const curr = points[points.length - 1];
          dotMarkers.push(
            new maps.Marker({
              position: { lat: (prev.lat() + curr.lat()) / 2, lng: (prev.lng() + curr.lng()) / 2 },
              map: map,
              clickable: false,
              icon: {
                path: maps.SymbolPath.CIRCLE,
                scale: 3,
                fillColor: "#ffffff",
                fillOpacity: 0.5,
                strokeColor: "transparent",
                strokeWeight: 0,
              },
            })
          );
        }

        if (points.length >= 2 && originalShape) {
          try {
            const reducedShape = originalShape.reduce((acc: number[][], shape) => [...acc, [shape.lat, shape.lng]], []);
            const fieldPolygon = turf.polygon([reducedShape]);
            const clickedPoint = turf.point([e.latLng.lat(), e.latLng.lng()]);
            if (!turf.booleanPointInPolygon(clickedPoint, fieldPolygon)) {
              completePolyline();
            }
          } catch {
            // auto-complete check failed silently
          }
        }
      }
    });
  };

  const split = async () => {
    setLoading(true);
    try {
      const res = await splitField({
        splitAreas,
        fieldId: field.id,
        splitDate: (splitDate || moment()).format("YYYY-MM-DD HH:mm:ss.SSSZ"),
      });

      navigate(
        `/farm/field/${res.splitFieldIds[0]}/${res.splitFieldIds[1]}/split-success`,
        isViewSwitcherEnabled ? { state: { returnTo: location.pathname } } : undefined
      );
    } catch (e) {
      setShowModal(false);
      const status = isAxiosError(e) ? e.response?.status : undefined;
      message({
        content: status === 400 ? t("global:errors.splitInvalidDate") : t("global:errors.splitSaveError"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
    setLoading(false);
  };

  const resetSplit = () => {
    setLoading(true);
    setShapes([originalShape]);
    setLineDrawn(false);
    setFieldSplit(false);
    setTimeout(() => setLoading(false), 10);
  };

  useEffect(() => {
    if (!fieldShape) {
      return;
    }
    const shapesCopy = shapes.length && shapes.length > 0 ? [...shapes] : [];
    shapesCopy.push(fieldShape?.geometry);
    setShapes(shapesCopy);
    setOriginalShape(fieldShape?.geometry);
  }, [fieldShape]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Drawer
        className="split-field"
        dataTestId="split-field-drawer"
        footer={
          <Row align="middle">
            <Col sm={12} md={12}>
              <Link onClick={() => onClose()}>{t("farm:fieldSplitting.cancel")}</Link>
            </Col>
            <Col sm={12} md={12}>
              <Button
                dataTestId="save-split-button"
                block
                disabled={!fieldSplit || !splitDate}
                loading={loading}
                type="primary"
                onClick={() => setShowModal(true)}
              >
                {t("farm:fieldSplitting.saveSplit")}
              </Button>
            </Col>
          </Row>
        }
        onClose={onClose}
        title={t("farm:fieldSplitting.splitField")}
        visible={visible}
      >
        <Flex vertical gap="1rem" className="split-field-container" style={{ height: "100%" }}>
          <div className="map-container">
            {!fieldSplit ? (
              <Typography variant="paragraph-m" className="center label top">
                {t("farm:fieldSplitting.drawLine")}
              </Typography>
            ) : null}
            {lineDrawn && fieldSplit ? (
              <Typography variant="paragraph-m" data-testid="reset-split-button" onClick={() => resetSplit()} className="center label top pointer">
                <Icon iconName="tabler/x" className="inline icon" />
                {t("farm:fieldSplitting.resetSplit")}
              </Typography>
            ) : null}

            {!loading && field && shapes && (
              // @ts-expect-error need to convert to tsx
              <MapInteractive
                defaultZoom={15}
                disableDrag={true}
                field={field}
                geometries={shapes}
                location={field.location}
                markers={
                  splitAreas.length > 1
                    ? splitAreas.map((area: Area) => ({
                        location: area.center,
                        fieldSize: area.area,
                      }))
                    : null
                }
                setAreas={setSplitAreas}
                setupDrawingAPI={setupDrawingAPI}
                split
                zoomControl={true}
              />
            )}
          </div>
          <Flex vertical gap="1rem" className="split-date-picker">
            <Typography variant="h5">{t("farm:fieldSplitting.splitDate")}</Typography>
            <DatePicker
              initialValue={moment()}
              onChange={setSplitDate}
              dataTestId="split-date"
              disabled={enforceSubmissionRestriction && restrictionLoading}
              disabledDate={enforceSubmissionRestriction ? isDisabledDate : (current) => current > moment().endOf("day")}
            />
          </Flex>
        </Flex>
      </Drawer>
      <Modal
        dataTestId="split-field-modal"
        cancelText={t("farm:fieldSplitting.cancel")}
        description={t("farm:fieldSplitting.modal.description")}
        onCancel={() => setShowModal(false)}
        onOk={() => split()}
        okText={t("farm:fieldSplitting.splitField")}
        showModal={showModal}
        title={t("farm:fieldSplitting.modal.heading")}
        loading={loading}
      />
    </>
  );
};
